import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  carouselSlidesApi,
  type CarouselSlide,
  type CreateCarouselSlidePayload,
  type ReorderSlidesPayload,
  type SlidePage,
  type UpdateCarouselSlidePayload,
} from '../api/carouselSlidesApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown) => ((err as Error)?.message) || 'Something went wrong';

const SLIDES_KEY = 'carousel-slides';

const slidesKey = (tenantId: string, solutionId: string, page: SlidePage) =>
  [SLIDES_KEY, tenantId, solutionId, page] as const;

// ── Surgical cache maintenance ──────────────────────────────────────────────
// The list is the ADMIN read (GET /carousel-slides/admin — the FULL group,
// inactive + out-of-window slides included), so a refetch is always
// authoritative. Mutations still patch the cached lists from their own
// responses rather than invalidating: every write returns the fresh
// slide/group, so patching keeps the UI instant without an extra round-trip.
//   create/update → upsert into the slide's (tenant, solution, page) list
//   delete        → remove from every cached list
//   reorder       → replace the group wholesale (the response is the FULL group,
//                   inactive slides included)

const byPosition = (a: CarouselSlide, b: CarouselSlide) => a.position - b.position;

const removeSlideEverywhere = (queryClient: QueryClient, id: string) => {
  const lists = queryClient.getQueriesData<CarouselSlide[]>({ queryKey: [SLIDES_KEY] });
  for (const [key, slides] of lists) {
    if (slides?.some((s) => s.id === id)) {
      queryClient.setQueryData<CarouselSlide[]>(key, slides.filter((s) => s.id !== id));
    }
  }
};

const upsertSlide = (queryClient: QueryClient, slide: CarouselSlide) => {
  // An update can move a slide to another page group — clear stale copies first.
  removeSlideEverywhere(queryClient, slide.id);
  queryClient.setQueryData<CarouselSlide[]>(
    slidesKey(slide.tenantId, slide.solutionId, slide.page),
    (old) => [...(old ?? []).filter((s) => s.id !== slide.id), slide].sort(byPosition),
  );
};

// ── Hooks ───────────────────────────────────────────────────────────────────

export const useCarouselSlides = (
  tenantId: string | null | undefined,
  solutionId: string | null | undefined,
  page: SlidePage,
) =>
  useQuery({
    queryKey: [SLIDES_KEY, tenantId, solutionId, page],
    queryFn: () => carouselSlidesApi.list(tenantId!, solutionId!, page),
    staleTime: 30 * 1000,
    enabled: !!tenantId && !!solutionId,
  });

export const useCreateSlide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateCarouselSlidePayload) => carouselSlidesApi.create(data),
    onSuccess: (slide) => {
      upsertSlide(queryClient, slide);
      toast.success('Slide created');
    },
    onError: (err) => toast.error(errMsg(err)),
  });
};

export const useUpdateSlide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCarouselSlidePayload }) =>
      carouselSlidesApi.update(id, data),
    onSuccess: (slide) => {
      upsertSlide(queryClient, slide);
      toast.success('Slide updated');
    },
    onError: (err) => toast.error(errMsg(err)),
  });
};

export const useDeleteSlide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => carouselSlidesApi.remove(id),
    onSuccess: (_result, id) => {
      removeSlideEverywhere(queryClient, id);
      toast.success('Slide deleted');
    },
    onError: (err) => toast.error(errMsg(err)),
  });
};

/**
 * Two-phase bulk reorder (PATCH /carousel-slides/reorder). The payload's slideIds
 * must be the complete ordered id list for the (tenant, solution, page) group;
 * the tenant travels as the x-tenant-id header via ApiFetch.
 */
export const useReorderSlides = (tenantId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: ReorderSlidesPayload) => carouselSlidesApi.reorder(data),
    onSuccess: (slides, variables) => {
      if (!tenantId) return;
      // The response is the full group in its new order — inactive slides included —
      // so it is authoritative for the cached list.
      queryClient.setQueryData<CarouselSlide[]>(
        slidesKey(tenantId, variables.solutionId, variables.page ?? 'HOME'),
        slides,
      );
    },
    onError: (err) => toast.error(errMsg(err)),
  });
};
